/**
 * UserSearchModal (AC-899)
 *
 * Reusable modal for searching and selecting users via typeahead.
 * Uses socialStore.searchUsers for debounced user discovery.
 *
 * Parent: TeamInviteDialog (and any component needing user selection)
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, Loader2, Users } from 'lucide-react';
import { useSocialStore } from '../../store/socialStore';
import { UserPublicSummary } from '../../types';

export interface UserSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectUser: (user: UserPublicSummary) => void;
  title?: string;
  placeholder?: string;
}

const UserSearchModal: React.FC<UserSearchModalProps> = ({
  isOpen,
  onClose,
  onSelectUser,
  title = 'Search Users',
  placeholder = 'Search by username...',
}) => {
  const { searchResults, searchLoading, searchUsers } = useSocialStore();
  const [query, setQuery] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Focus input when modal opens; reset state when closed
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setHasSearched(false);
      // Small delay to let the modal render before focusing
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  // Escape key closes the modal
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const debouncedSearch = useCallback(
    (value: string) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      if (value.trim().length < 2) {
        setHasSearched(false);
        return;
      }
      debounceRef.current = setTimeout(() => {
        setHasSearched(true);
        searchUsers(value.trim());
      }, 300);
    },
    [searchUsers],
  );

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    debouncedSearch(value);
  };

  const handleSelectUser = (user: UserPublicSummary) => {
    onSelectUser(user);
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="user-search-title"
      onClick={handleBackdropClick}
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2
            id="user-search-title"
            className="text-lg font-semibold text-gray-900 dark:text-white"
          >
            {title}
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search Input */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={handleQueryChange}
              placeholder={placeholder}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors"
              aria-label="Search users"
            />
          </div>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Loading state */}
          {searchLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                Searching...
              </span>
            </div>
          )}

          {/* Empty state: no query yet */}
          {!searchLoading && !hasSearched && (
            <div className="flex flex-col items-center justify-center py-8 text-gray-400 dark:text-gray-500">
              <Users className="w-8 h-8 mb-2" />
              <p className="text-sm">Type at least 2 characters to search</p>
            </div>
          )}

          {/* Empty state: searched but no results */}
          {!searchLoading && hasSearched && searchResults.length === 0 && (
            <p className="text-center text-sm text-gray-500 dark:text-gray-400 py-8">
              No users found matching &quot;{query.trim()}&quot;
            </p>
          )}

          {/* Results list */}
          {!searchLoading && hasSearched && searchResults.length > 0 && (
            <div className="space-y-1">
              {searchResults.map((user) => (
                <button
                  key={user.user_id}
                  onClick={() => handleSelectUser(user)}
                  className="w-full flex items-center justify-between p-3 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      @{user.username}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {user.public_project_count} public project{user.public_project_count !== 1 ? 's' : ''} &middot;{' '}
                      {user.follower_count} follower{user.follower_count !== 1 ? 's' : ''}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserSearchModal;
